import { CamelCasedProperties, SetNonNullable } from "type-fest";

import { SemanticDatasetName } from "../../idTypeBrands.js";
import { stableEmptyArray } from "../../stableObjs.js";
import { typedKeyBy } from "../../utils/typedObjects.js";
import * as HexSLTypes from "../generated/hex_sl_schema.js";
import {
  semanticDatasetTitleString,
  semanticDimensionTitleString,
  semanticMeasureTitleString,
} from "../semanticTitleUtils.js";

export interface SemanticViewHydratedContentGroup extends SetNonNullable<
  Omit<
    Required<CamelCasedProperties<HexSLTypes.ViewContentGroup>>,
    "dimensions" | "measures" | "contents"
  >,
  "title"
> {
  dataset: Pick<
    HexSLTypes.Dataset,
    "name" | "title" | "description" | "hidden"
  >;
  dimensions: SemanticViewHydratedContentGroupDimension[];
  measures: SemanticViewHydratedContentGroupMeasure[];
  contents: SemanticViewHydratedContentGroup[];
  depth: number;
  parentGroup: ParentGroup | null;
}

type SemanticViewHydratedContentGroupItemBase = Omit<
  Omit<
    Required<CamelCasedProperties<HexSLTypes.ViewContentGroupItem>>,
    "title"
  >,
  "queryPathCanonicalDatasetName"
> & {
  queryPathCanonicalDatasetName: SemanticDatasetName;
  /** The final title (content group item title ?-> dimension title ?-> dimension name) */
  title: string;
  item: HexSLTypes.ViewContentGroupItem;
  parentGroup: ParentGroup | null;
};

type ParentGroup = Pick<HexSLTypes.ViewContentGroup, "title"> & {
  depth: number;
};

export interface SemanticViewHydratedContentGroupDimension extends SemanticViewHydratedContentGroupItemBase {
  dimension: HexSLTypes.Dimension;
  parentGroup: ParentGroup;
}

export interface SemanticViewHydratedContentGroupMeasure extends SemanticViewHydratedContentGroupItemBase {
  measure: HexSLTypes.Measure;
  parentGroup: ParentGroup;
}

export type SemanticViewHydratedContents = SemanticViewHydratedContentGroup[];

/**
 * Produce a copy of the given view contents with the original dataset(s),
 * dimension(s), and measure(s) as an additional property. Titles and
 * descriptions are derived from the view override, or the original properties.
 */
export function getSemanticViewHydratedContents({
  contents,
  datasets,
}: {
  contents: HexSLTypes.ViewContents;
  datasets: readonly Pick<
    HexSLTypes.Dataset,
    "name" | "title" | "description" | "dimensions" | "measures" | "hidden"
  >[];
}): SemanticViewHydratedContents {
  const datasetsByName = typedKeyBy(datasets, "name");
  const getDataset: GetDataset = (datasetName) => {
    return datasetsByName[datasetName] ?? null;
  };

  const dimensionsByNameByDataset: Record<
    SemanticDatasetName,
    Record<HexSLTypes.DimensionName, HexSLTypes.Dimension | undefined>
  > = {};
  const getDimension: GetDimension = (datasetName, dimensionName) => {
    const dataset = getDataset(datasetName);
    if (dataset == null) return null;
    let dimensionsByName = dimensionsByNameByDataset[datasetName];
    if (dimensionsByName == null) {
      dimensionsByName = typedKeyBy(dataset.dimensions ?? ([] as []), "name");
      dimensionsByNameByDataset[datasetName] = dimensionsByName;
    }
    return dimensionsByName[dimensionName] ?? null;
  };

  const measuresByNameByDataset: Record<
    SemanticDatasetName,
    Record<HexSLTypes.MeasureName, HexSLTypes.Measure | undefined>
  > = {};
  const getMeasure: GetMeasure = (datasetName, measureName) => {
    const dataset = getDataset(datasetName);
    if (dataset == null) return null;
    let measuresByName = measuresByNameByDataset[datasetName];
    if (measuresByName == null) {
      measuresByName = typedKeyBy(dataset.measures ?? ([] as []), "name");
      measuresByNameByDataset[datasetName] = measuresByName;
    }
    return measuresByName[measureName] ?? null;
  };

  const convertContentGroupToHydratedGroup = ({
    contentGroup: contentGroup_,
    depth,
    parentGroup,
  }: {
    contentGroup: HexSLTypes.ViewContentGroup;
    depth: number;
    parentGroup:
      | (HexSLTypes.ViewContentGroup & {
          depth: number;
        })
      | null;
  }): SemanticViewHydratedContentGroup => {
    const contentGroup = { ...contentGroup_, depth };

    const queryPath = (contentGroup.query_path ?? []) as SemanticDatasetName[];
    const queryPathCanonicalDatasetName = SemanticDatasetName.check(
      contentGroup.query_path_canonical_dataset_name,
    );
    const dataset = getDataset(queryPathCanonicalDatasetName);

    if (dataset == null) {
      throw new Error(
        `Dataset in view not found for query path: ${queryPath} (${queryPathCanonicalDatasetName})`,
      );
    }

    const hydratedDimensions = (contentGroup.dimensions ?? []).map((d) =>
      getHydratedDimension({
        dimensionRef: d,
        getDimension,
        parentGroup: contentGroup,
      }),
    );

    const hydratedMeasures = (contentGroup.measures ?? []).map((m) =>
      getHydratedMeasure({
        measureRef: m,
        getMeasure,
        parentGroup: contentGroup,
      }),
    );

    // recurse through nested content groups
    const hydratedContents = (contentGroup.contents ?? []).map(
      (nestedContentGroup) =>
        convertContentGroupToHydratedGroup({
          contentGroup: nestedContentGroup,
          depth: depth + 1,
          parentGroup: contentGroup,
        }),
    );

    return {
      dataset: dataset,
      depth,
      queryPath: queryPath,
      queryPathCanonicalDatasetName: queryPathCanonicalDatasetName,
      title: contentGroup.title ?? semanticDatasetTitleString(dataset),
      description: contentGroup.description ?? dataset.description ?? null,
      dimensions: hydratedDimensions,
      measures: hydratedMeasures,
      contents: hydratedContents,
      parentGroup,
    };
  };

  return contents.map((cg) =>
    convertContentGroupToHydratedGroup({
      contentGroup: cg,
      depth: 0,
      parentGroup: null,
    }),
  );
}

function getHydratedDimension({
  dimensionRef,
  getDimension,
  parentGroup,
}: {
  dimensionRef: HexSLTypes.ViewContentGroupItem;
  getDimension: GetDimension;
  parentGroup: ParentGroup;
}): SemanticViewHydratedContentGroupDimension {
  const queryPathCanonicalDatasetName = SemanticDatasetName.check(
    dimensionRef.query_path_canonical_dataset_name,
  );

  const dimension = getDimension(
    queryPathCanonicalDatasetName,
    dimensionRef.item_name,
  );

  if (dimension == null) {
    throw new Error(
      `Dimension in view, item name: ${dimensionRef.item_name}, not found for query path: ${dimensionRef.query_path} (${dimensionRef.query_path_canonical_dataset_name})`,
    );
  }

  return {
    dimension,
    item: dimensionRef,
    itemName: dimensionRef.item_name,
    queryPath: dimensionRef.query_path ?? (stableEmptyArray() as []),
    queryPathCanonicalDatasetName,
    title: dimensionRef.title ?? semanticDimensionTitleString(dimension),
    description: dimensionRef.description ?? dimension.description ?? null,
    parentGroup,
  };
}

function getHydratedMeasure({
  getMeasure,
  measureRef,
  parentGroup,
}: {
  getMeasure: GetMeasure;
  measureRef: HexSLTypes.ViewContentGroupItem;
  parentGroup: ParentGroup;
}): SemanticViewHydratedContentGroupMeasure {
  const queryPathCanonicalDatasetName = SemanticDatasetName.check(
    measureRef.query_path_canonical_dataset_name,
  );

  const measure = getMeasure(
    queryPathCanonicalDatasetName,
    measureRef.item_name,
  );

  if (measure == null) {
    throw new Error(
      `Measure in view, item name: ${measureRef.item_name}, not found for query path: ${measureRef.query_path} (${measureRef.query_path_canonical_dataset_name})`,
    );
  }

  return {
    measure,
    item: measureRef,
    itemName: measureRef.item_name,
    queryPath: measureRef.query_path ?? (stableEmptyArray() as []),
    queryPathCanonicalDatasetName,
    title: measureRef.title ?? semanticMeasureTitleString(measure),
    description: measureRef.description ?? measure.description ?? null,
    parentGroup,
  };
}

type GetDataset = (
  datasetName: SemanticDatasetName,
) => Pick<
  HexSLTypes.Dataset,
  "name" | "title" | "description" | "dimensions" | "measures" | "hidden"
> | null;

type GetDimension = (
  datasetName: SemanticDatasetName,
  dimensionName: HexSLTypes.DimensionName,
) => HexSLTypes.Dimension | null;

type GetMeasure = (
  datasetName: SemanticDatasetName,
  measureName: HexSLTypes.MeasureName,
) => HexSLTypes.Measure | null;
